import { DeepReadonly } from '../infrastructure/type-extensions';
import { MeetingsStatus } from '../components/_zoom/_tracked-users-list/meeting-status.enum';
import { JobDtoType } from '../domain/jobs';
import { ApprovalType } from '../components/_events/_manage-event/shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { IEventRequestParams, ISortRequestParams } from './shared.dto';
import { IConnectionAttendeeDTO, IConnectionDetailsDTO, IMeetingConnectionRequestParams, WebinarAutoRecording } from './connections.dao';

abstract class IZoomDAO {
    public abstract getZoomUsers(pageSize: number, pageNumber: number): Promise<IZoomUsersResponse>;
    public abstract getZoomMeetingDetails(meetingId: number): Promise<IZoomMeetingListItemDTO>;
    public abstract getZoomConnectionDetails(zoomId: string): Promise<IZoomMeetingListItemDTO>;
    public abstract getTrackedUsers(params: IZoomUsersRequest): Promise<ITrackedZoomUsersListDTO>;
    public abstract updateTrackedUsers(updateUsersRequest: Partial<DeepReadonly<IUpdateZoomUsersRequest>>): Promise<void>;
    public abstract getScheduledMeetings(params: IScheduledMeetingsListRequest): Promise<IScheduledMeetingsListDTO>;
    public abstract getScheduledMeetingDetails(meetingId: number): Promise<IScheduledZoomMeetingDetailsDTO>;
    public abstract getUpdatedMeetingDetails(params: IMeetingConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO>;
    public abstract scheduleNewMeeting(params: IZoomMeetingDTO): Promise<IZoomMeeting>;
    public abstract scheduleNewRecurringMeeting(params: IRecurringZoomMeetingDTO): Promise<IZoomMeeting>;
    public abstract updateMeeting(meetingId: number, params: IZoomMeetingDTO): Promise<IZoomMeeting>;
    public abstract deleteScheduledMeeting(meetingId: string): Promise<void>;
    public abstract refreshAllZoomMeetings(): Promise<JobDtoType>;
    public abstract refreshHostKeys(): Promise<JobDtoType>;
    public abstract refreshUserMeetingsSchedule(userZoomId: string): Promise<void>;
    public abstract refreshWebinars(userZoomId: string): Promise<void>;
    public abstract getUserScheduledMeetingsStatus(userZoomId: string): Promise<IScheduledZoomMeetingsStatusDTO>;
    public abstract getGroups(): Promise<IZoomGroupDTO[]>;
    public abstract getWebinarAttendeeReport(params: IEventRequestParams & { webinarId: string }): Promise<void>;
    public abstract getWebinarRegistrationReport(params: IEventRequestParams & { webinarId: string }): Promise<void>;
    public abstract getScheduledWebinarDetails(webinarId: number): Promise<IScheduledZoomWebinarDetailsDTO>;
    public abstract scheduleNewWebinar(params: IEventRequestParams & IZoomMeetingDTO): Promise<IZoomMeeting>;
    public abstract updateWebinar(
        params: IEventRequestParams & { webinarId: string } & IZoomMeetingDTO
    ): Promise<IZoomMeeting>;
    public abstract deleteWebinardMeeting(params: IEventRequestParams & { webinarId: string }): Promise<void>;
    public abstract getZoomWebinarReports(params: IEventRequestParams): Promise<IZoomWebinarReportDTO[]>;
    public abstract getZoomWebinarDetails(params: IEventRequestParams & { webinarId: string }): Promise<IScheduledZoomWebinarDetailsDTO>;

    public abstract updateZoomUserHostKey(params: { userZoomId: string }): Promise<JobDtoType>;
    public abstract getTrackedZoomUser(params: { userZoomId: string }): Promise<ITrackedZoomUserDTO>;
    public abstract getSingleZoomMeetingDetails(params: { meetingId: string }): Promise<IScheduledZoomMeetingDetailsDTO>;
    public abstract getTelephoneNumberSets(params: ITelephoneNumberSetRequest): Promise<ITelephoneNumberSetsListDTO>;
    public abstract createTelephoneNumberSet(numberSetDetails: IRequestTelephoneNumberSet): Promise<ITelephoneNumberSetsListDTO>;
    public abstract updateTelephoneNumberSet(numberSetDetails: IUpdateNumberSetDTO): Promise<ITelephoneNumberSetsListDTO>;
    public abstract deactivateTelephoneNumberSet(params: {audioProviderId: string}): void;
    public abstract fetchTelephoneNumberSet(params: {audioProviderId: string}): Promise<IZoomTelephoneNumberSetDTO>;
    public abstract createUniqueDialInConnection(params: UniqueDialInRequestParams): Promise<UniqueDialInData>;
    public abstract downloadTelephoneNumberSet(params: {audioProviderId: string}): Promise<void>;
    public abstract deleteScheduledAudioBridge(params: {audioBridgeId: string}): Promise<void>;
    public abstract deleteScheduledPanelistLink(params: PanelistLinkDeletionRequestParams): Promise<void>;
    public abstract getZoomMeetingRegistrationReport(params: IEventRequestParams & { meetingId: string }): Promise<void>;
    public abstract getPhoneNumbersSet(params: {audioProviderId: string}): Promise<IAudioProviderInfoDTO>;
}

interface ITrackedZoomUsersListDTO {
    totalElements: number;
    content: ITrackedZoomUserDTO[];
}
interface ITrackedZoomUserDTO {
    email: string;
    firstName: string;
    lastName: string;
    groups: IZoomGroup[];
    zoomId: string;
    hostKey?: string;
}

interface IZoomGroupDTO {
    name: string;
    zoomGroupId: number;
    zoomId?: string;
}

interface IZoomMeetingDTO {
    startTime: Date;
    endTime: Date;
    timeZone: string;
    topic: string;
    agenda?: string;
    zoomId?: string;
    zoomGroupId?: number;
    settings?: {
        approvalType?: ApprovalType;
        registrantsConfirmationEmail?: boolean;
        autoRegistrationEnabled?: boolean;
        autoSyncEnabled?: boolean;
        autoRecording?: WebinarAutoRecording;
    };
    type?: string;
    accountId?: number;
}

interface IRecurringZoomMeetingDTO {
    topic: string;
    email: string;
    username: string;
    zoomGroupId: number;
    type: string;
    accountId: number;
}

interface IZoomUser {
    created_at: string;
    dept: string;
    email: string;
    first_name: string;
    id: string;
    language: string;
    last_login_time: string;
    last_name: string;
    phone_number: string;
    pmi: number;
    status: string;
    timezone: string;
    type: number;
    verified: number;
    groups: IZoomGroup[];
    tracked?: boolean;
    hostKey: string;
}

interface IZoomGroup {
    name: string;
    zoomGroupId: number;
    zoomId: string;
}

interface IZoomMeeting {
    endTime: string;
    id: number;
    startTime: string;
    timeZone: string;
    topic: string;
    zoomId: string;
    zoomUser: IZoomUser;
}

interface IZoomUsersResponse {
    page_count: number;
    page_number: number;
    page_size: number;
    total_records: number;
    users: IZoomUser[];
}

interface IUpdateZoomUsersRequest {
    addItems: ITrackedZoomUserDTO[];
    deleteItems: string[];
}

interface IScheduledMeetingsListDTO {
    content: IZoomMeetingListItemDTO[];
    totalElements: number;
    size: number;
}

interface IZoomMeetingListItemDTO {
    id: number;
    startTime: Date | null;
    endTime: Date | null;
    timeZone: string;
    topic: string;
    zoomId: string;
    zoomUser: ITrackedZoomUserDTO;
    type: string;
    registrantsConfirmationEmail?: boolean;
    approvalType?: ApprovalType;
}

type TScheduledZoomMeetingsListRequestSortKeys =
    keyof Omit<IZoomMeetingListItemDTO, 'zoomUser'> | 'zoomUser.email' | 'zoomUser.firstName' | 'zoomUser.lastName';

interface IScheduledZoomMeetingDetailsDTO extends IZoomMeetingListItemDTO {
    password: string;
    joinUrl: string;
    startUrl: string;
    status: string;
    agenda: string;
    invitation: string;
    autoRecording?: WebinarAutoRecording;
    zoomGroupId?: number;
    sip?: string;
    registrantsConfirmationEmail?: boolean;
    approvalType?: ApprovalType;
    autoRegistrationEnabled?: boolean;
    autoSyncEnabled?: boolean;
    registrationUrl?: string;
}

interface IScheduledZoomWebinarDetailsDTO extends IScheduledZoomMeetingDetailsDTO {
    description?: string;
}

interface IScheduledZoomMeetingsStatusDTO {
    id: number;
    pageCount: number;
    pageNumber: number;
    startedAt: string;
    status: MeetingsStatus;
    updatedAt: string;
    zoomUser: ITrackedZoomUserDTO;
}

interface IScheduledMeetingsListRequest extends ISortRequestParams<TScheduledZoomMeetingsListRequestSortKeys> {
    startDateTime: Date;
    endDateTime: Date;
    query: string;
    page: number;
    size: number;
}
interface IZoomUsersRequest extends ISortRequestParams<TTrackedUsersListRequestSortKeys> {
    query: string;
    page: number;
    size: number;
}

interface IZoomWebinarReportDTO {
    startDateTime: Date;
    topic: string;
    roomName: string;
    track: string;
    meetingType: string;
    presenters: string[];
    connectionId: string;
}

type TTrackedUsersListRequestSortKeys =
    keyof Omit<ITrackedZoomUsersListDTO, 'zoomUser'> | 'email' | 'firstName' | 'lastName';

interface IWebinarRequestParams extends Pick <
    IEventRequestParams,
    'accountId' | 'eventId'
    > {
        webinarId: string;
}

interface IZoomMeetingRequestParams extends Pick <
    IEventRequestParams,
    'accountId' | 'eventId'
    > {
        meetingId: string;
}

enum ZoomMeetingType {
    SCHEDULED_MEETING = 'SCHEDULED',
    RECURRING_NO_FIXED_MEETING = 'RECURRING_NO_FIXED_TIME',
    WEBINAR = 'WEBINAR'
};

interface IZoomTelephoneNumberSetDTO {
    id?: string;
    name: string;
    provider: string;
    oneTapFormula?: string;
    telephoneNumbersCount?: number;
    telephoneArchive?: null | File;
}

interface ITelephoneNumberSetsListDTO {
    totalElements: number;
    content: IZoomTelephoneNumberSetDTO[];
}
type TTelephoneNumberSetsRequestSortKeys = 'name' | 'provider' | 'telephoneNumbersCount';

interface ITelephoneNumberSetRequest extends ISortRequestParams<TTelephoneNumberSetsRequestSortKeys> {
    query: string;
    page: number;
    size: number;
}

interface ITelephoneFormDTO extends Partial<IZoomTelephoneNumberSetDTO> {
    title: string;
}

interface IRequestTelephoneNumberSet {
    name: string;
    provider: string;
    oneTapFormula?: string;
    telephoneArchive: null | File;
}
interface IUpdateNumberSetDTO {
    id?: string;
    name: string;
    provider: string;
    oneTapFormula?: string;
    telephoneArchive?: File | null;
}

interface NumberSetRequestDataWithFile extends NumberSetRequestProviderData {
    file: Blob;
}

interface NumberSetRequestProviderData {
    audioProvider: Blob;
    id: number;
}

interface UniqueDialInDataDetails {
    audioBridgeAttendeeId: string;
    externalAttendeeId: string;
    conferenceId: string;
    attendeeJoinId: string;
    uniqueDialInCode: string;
}
interface UniqueDialInData {
    attendeeUniqueJoinIdInfos: UniqueDialInDataDetails[];
}

interface UniqueDialInRequestParams {
    accountId: number;
    eventId: number;
    audioBridgeId: string;
    attendees: Partial<IConnectionAttendeeDTO>[];
}

interface PanelistLinkDeletionRequestParams {
    accountId: number;
    eventId: number;
    webinarId: string;
    panelistEmail: string;
}

interface IPhoneNumbersSet {
    priority: number;
    country: string;
    countryCode: string;
    telephoneNumber: string;
    unformattedTelephoneNumber: string;
    toll: string;
    oneTapFormula: string;
}

interface ISortedPhoneNumbers {
    [country: string]: IPhoneNumbersSet[];
}

interface IAudioProviderInfoDTO {
    id: number;
    name: string;
    oneTapFormula: string;
    provider: string;
    telephoneNumbers: IPhoneNumbersSet[];
    telephoneNumbersCount: number;
}

export {
    IZoomUser,
    IZoomGroup,
    IZoomGroupDTO,
    IZoomUsersRequest,
    IZoomUsersResponse,
    ITrackedZoomUserDTO,
    ITrackedZoomUsersListDTO,
    IUpdateZoomUsersRequest,
    IZoomDAO,
    IZoomMeeting,
    IScheduledZoomWebinarDetailsDTO,
    IZoomMeetingListItemDTO,
    IScheduledMeetingsListDTO,
    IScheduledZoomMeetingDetailsDTO,
    IZoomMeetingDTO,
    IScheduledZoomMeetingsStatusDTO,
    IScheduledMeetingsListRequest,
    TScheduledZoomMeetingsListRequestSortKeys,
    TTrackedUsersListRequestSortKeys,
    IZoomWebinarReportDTO,
    IWebinarRequestParams,
    IZoomMeetingRequestParams,
    ZoomMeetingType,
    IRecurringZoomMeetingDTO,
    ITelephoneNumberSetsListDTO,
    TTelephoneNumberSetsRequestSortKeys,
    ITelephoneNumberSetRequest,
    IZoomTelephoneNumberSetDTO,
    ITelephoneFormDTO,
    IRequestTelephoneNumberSet,
    IUpdateNumberSetDTO,
    NumberSetRequestDataWithFile,
    NumberSetRequestProviderData,
    UniqueDialInData,
    UniqueDialInRequestParams,
    PanelistLinkDeletionRequestParams,
    IPhoneNumbersSet,
    IAudioProviderInfoDTO,
    ISortedPhoneNumbers
};
